/* eslint no-underscore-dangle: ["error", { "allow": ["__INITIAL_STATE__", "__REDUX_STORE__", "__ZUSTAND_STORE__"] }] */
import { applyMiddleware, legacy_createStore as createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import { thunk } from 'redux-thunk';
import omit from 'lodash/omit';
import { create } from 'zustand';
import { getUrlParam } from 'Utils/Content';
import { configureAuthStore, isNonAnonymousUser, isAnonymousUser, getUserMvpdId, isMvpdUser } from 'Utils/UserCookie';
import { userInitialState } from 'State/User';
import { logger } from 'Utils/Logger';
import reducers from './reducers';
import zustandInitialState from './zustandInitialState';
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';

const isSSR = getUrlParam('isSSR', true);

const getDefaultState = (state) => {
  let defaultState = {};
  if (state) {
    const { plans, pageLayout, ...rest } = state;
    if (pageLayout && pageLayout.footerContent) {
      pageLayout.footerContent.isLoaded = true;
      defaultState.pageLayout = pageLayout;
    }
    // to always refetch plans on client
    if (plans) {
      plans.getPlansSuccess = false;
      plans.isFetchingPlans = false;
      defaultState.plans = plans;
    }

    defaultState = { ...rest, ...defaultState };
  }
  return defaultState;
};

const getAppConfig = () => {
  const screens = GLOBAL_SCREENS;
  const routes = GLOBAL_ROUTES;
  const strings = GLOBAL_STRINGS;
  const defaultConfig = GLOBAL_CONFIG;
  const pageMeta = GLOBAL_PAGEMETA;

  return { screens, strings, routes, defaultConfig, pageMeta };
};

const getInitialUserState = (config) => {
  try {
    // init auth store first time
    const token = configureAuthStore(config);
    const isAnonymous = isAnonymousUser();
    const isAuth = token && isNonAnonymousUser();
    if (isAuth) {
      // identity account type
      // remove accessToken and tokenExp before initial redux state.
      const user = omit(token, ['accessToken', 'tokenExpiration', 'accessTokenExpiration']);
      logger.setAttr('profile_id', user.profileId);
      return {
        ...userInitialState,
        isAuthenticated: true,
        ...user,
      };
    }
    if (isAnonymous) {
      const user = omit(token, ['accessToken', 'tokenExpiration', 'accessTokenExpiration']);
      const { preauthorizedResources = [] } = user;
      const hasAuthorizedResources = preauthorizedResources.includes(config.defaultConfig.entitlementId);
      const hasMvpd = hasAuthorizedResources && isMvpdUser();
      const mvpdId = (hasMvpd && getUserMvpdId()) || '';
      return {
        ...userInitialState,
        isAnonymousUser: true,
        hasMvpd,
        mvpdId,
        ...user,
      };
    }
  } catch (e) {
    console.log(e);
  }
  return undefined;
};

//TODO: remove this once zustand integration done
const reduxOrZustandFilter = (state = {}, isZustand = false) => {
  let filteredState;
  const zustandStoreKeys = Object.keys(zustandInitialState);

  if (isZustand) {
    filteredState = pick(state, zustandStoreKeys);
  } else {
    filteredState = pickBy(state, (value, key) => !zustandStoreKeys.includes(key));
  }
  return filteredState;
};

const getInitialState = (state = {}) => {
  const defaultState = getDefaultState(state);
  const appConfig = getAppConfig();
  const user = getInitialUserState(appConfig);
  if (user) defaultState.user = user;
  let initialState = { ...defaultState, appConfig };

  return initialState;
};

// to be invoked once per app lifecycle
const configureStore = () => {
  const initialState = getInitialState(window.__INITIAL_STATE__);
  const filteredState = reduxOrZustandFilter(initialState);
  return createStore(reducers, filteredState, composeWithDevTools(applyMiddleware(thunk)));
};

// to be invoked once per app lifecycle
const configureZustandStore = () => {
  const initialState = getInitialState(window.__INITIAL_STATE__);
  const filteredState = reduxOrZustandFilter(initialState, true);

  return create(() => ({ ...zustandInitialState, ...filteredState }));
};

export let useZustandStore = configureZustandStore();

// for use in tests only
export const createMockStore = (customInitialState = {}) => {
  const initialState = getInitialState(window.__INITIAL_STATE__);

  const mockInitialState = {
    purchaseMode: {
      name: 'NEW_PLAN',
      isChangePlan: false,
      getPlansOnWelcome: true,
      showPlanDescOnPaymentReview: false,
      isPlanPayment: true,
      requirePlanSelection: true,
    },
    user: { isAuthenticated: true, email: 'test@fox.com' },
  };

  const combinedState = { ...initialState, ...mockInitialState, ...customInitialState };

  //zustand store
  const zustandFilteredState = reduxOrZustandFilter(combinedState, true);
  useZustandStore = create(() => ({ ...zustandInitialState, ...zustandFilteredState }));

  //redux store
  const reduxState = reduxOrZustandFilter(combinedState);
  return createStore(reducers, reduxState, applyMiddleware(thunk));
};

const store = configureStore();

if (isSSR) {
  // required for server side rendering
  window.__REDUX_STORE__ = store;
  window.__ZUSTAND_STORE__ = useZustandStore;
}

export default store;
